/**
 * Copyright 2026 The Ground Authors.
 *
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, effect, inject, input, signal } from '@angular/core';
import { Storage, getDownloadURL, ref } from '@angular/fire/storage';

@Component({
  selector: 'submission-photo-view',
  templateUrl: './submission-photo-view.component.html',
  styleUrls: ['./submission-photo-view.component.scss'],
  standalone: false,
})
export class SubmissionPhotoViewComponent {
  private storage = inject(Storage);

  path = input.required<string | null>();

  readonly downloadUrl = signal<string | undefined>(undefined);
  readonly unavailable = signal(false);

  constructor() {
    effect(onCleanup => {
      const path = this.path();
      this.downloadUrl.set(undefined);
      this.unavailable.set(false);
      if (!path || path.trim() === '' || path === '/') {
        console.warn('Photo task has no valid image path.');
        this.unavailable.set(true);
        return;
      }
      let active = true;
      onCleanup(() => {
        active = false;
      });
      getDownloadURL(ref(this.storage, path))
        .then(url => {
          if (active) this.downloadUrl.set(url);
        })
        .catch(error => {
          console.error(`Could not load image: ${path}`, error);
          if (active) this.unavailable.set(true);
        });
    });
  }
}
